/**
 * Issue 1 — Translate well-known SEEDED data values (department names,
 * position titles, competency names/descriptions, suite names) for ES.
 *
 * Data rows come from the backend as English-only strings. This hook returns
 * the Spanish equivalent when the user is in ES mode, or the original string
 * otherwise. Unknown values fall through unchanged.
 *
 * Usage:
 *   const tData = useDataLabel();
 *   <td>{tData(d.name)}</td>
 *   <td>{tData(u.position)}</td>
 *   <td>{tData(c.description)}</td>
 */
import { useTranslation } from 'react-i18next';

const norm = (s) =>
  (s || '').toString().trim().toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[—–-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

// Lookup map. Keys are normalized English; values are Spanish.
const ES_MAP = {
  // — Departments —
  'finance': 'Finanzas',
  'finance & logistics operations': 'Finanzas y Operaciones Logísticas',
  'operations': 'Operaciones',
  'commercial': 'Comercial',
  'product': 'Producto',
  'nursing': 'Enfermería',
  'engineering': 'Ingeniería',
  'human resources': 'Recursos Humanos',
  'hr': 'RR. HH.',
  'sales': 'Ventas',
  'marketing': 'Marketing',
  'it': 'TI',
  'technology': 'Tecnología',
  'logistics': 'Logística',

  // — Positions / job titles —
  'financial manager': 'Gerente Financiero',
  'financial analyst': 'Analista Financiero',
  'operations lead': 'Líder de Operaciones',
  'operations manager': 'Gerente de Operaciones',
  'commercial director': 'Director Comercial',
  'managing partner': 'Socio Director',
  'senior consultant': 'Asesor Senior',
  'senior advisor':    'Asesor Senior',
  'consultant':        'Asesor',
  'advisor':           'Asesor',
  'chief people officer': 'Director de Personas',
  'report reviewer': 'Revisor de Reportes',
  'product manager': 'Gerente de Producto',
  'store manager': 'Gerente de Tienda',
  'inventory coordinator': 'Coordinador de Inventario',
  'cashier lead': 'Líder de Caja',
  'nurse manager': 'Gerente de Enfermería',
  'medical records specialist': 'Especialista en Registros Médicos',
  'patient care coordinator': 'Coordinador de Atención al Paciente',
  'manager': 'Gerente',
  'coordinator': 'Coordinador',
  'analyst': 'Analista',
  'specialist': 'Especialista',
  'senior specialist': 'Especialista Senior',
  'director': 'Director',
  'lead': 'Líder',
  'associate': 'Asociado',

  // — Suites —
  'talentmap': 'TalentMap',
  'talentcheck': 'TalentCheck',
  'talentstart': 'TalentStart',
  'talentheart': 'TalentHeart',
  'talentfix': 'TalentFix',
  'talentcompetency': 'TalentCompetency',
  'talenttrain': 'TalentTrain',
  'climara': 'Climara',
  'climara work climate pulse': 'Climara — Pulso de Clima Laboral',
  'talentcompetency role benchmark': 'TalentCompetency — Benchmark de Puesto',
  'talenttrain learning needs assessment': 'TalentTrain — Evaluación de Necesidades de Aprendizaje',
  'talent intelligence': 'TalentIntelligence',

  // — Competencies (names) —
  'adaptability': 'Adaptabilidad',
  'communication': 'Comunicación',
  'conflict management': 'Manejo de Conflictos',
  'decision making': 'Toma de Decisiones',
  'development orientation': 'Orientación al Desarrollo',
  'emotional intelligence': 'Inteligencia Emocional',
  'financial acumen': 'Visión Financiera',
  'leadership': 'Liderazgo',
  'learning agility': 'Agilidad de Aprendizaje',
  'logical reasoning': 'Razonamiento Lógico',
  'motivation': 'Motivación',
  'organizational commitment': 'Compromiso Organizacional',
  'role alignment': 'Alineación con el Rol',
  'skill gap awareness': 'Conciencia de Brechas',
  'soft skills': 'Habilidades Blandas',
  'strategic thinking': 'Pensamiento Estratégico',
  'stress tolerance': 'Tolerancia al Estrés',
  'team cohesion': 'Cohesión de Equipo',
  'technical proficiency': 'Competencia Técnica',
  'workload balance': 'Balance de Carga',
  'teamwork': 'Trabajo en Equipo',
  'attention to detail': 'Atención al Detalle',
  'self management': 'Autogestión',
  'analytical reasoning': 'Razonamiento Analítico',

  // — Competency descriptions —
  'mastery of financial analysis, capital allocation, and budget control.':
    'Dominio del análisis financiero, asignación de capital y control presupuestario.',
  'quality, speed, and structure of judgement under uncertainty.':
    'Calidad, rapidez y estructura del juicio bajo incertidumbre.',
  'ability to direct, develop, and energize teams.':
    'Capacidad para dirigir, desarrollar y energizar equipos.',
  'long horizon framing and option value reasoning.':
    'Encuadre de largo plazo y razonamiento de valor de opciones.',
  'clarity, structure, and influence of verbal and written messages.':
    'Claridad, estructura e influencia en mensajes verbales y escritos.',
  'self awareness, empathy, and interpersonal regulation.':
    'Autoconocimiento, empatía y regulación interpersonal.',
  'ability to surface, navigate and resolve interpersonal friction.':
    'Capacidad de identificar, abordar y resolver fricción interpersonal.',
  'identification with, and loyalty to, the organisation.':
    'Identificación y lealtad hacia la organización.',
  'depth of role critical technical knowledge and execution.':
    'Profundidad de conocimiento técnico crítico para el rol y ejecución.',
  'interpersonal effectiveness in collaboration and influence.':
    'Eficacia interpersonal en colaboración e influencia.',
  'fit between demonstrated capabilities and the role\'s expectations.':
    'Ajuste entre las capacidades demostradas y las expectativas del rol.',
  'speed and quality of response to change and ambiguity.':
    'Velocidad y calidad de respuesta al cambio y la ambigüedad.',
  'capacity to rapidly absorb, apply and re use new knowledge.':
    'Capacidad de absorber, aplicar y reutilizar nuevos conocimientos rápidamente.',
  'self diagnostic clarity on where development is needed most.':
    'Claridad autodiagnóstica sobre dónde se necesita más desarrollo.',
  'willingness to invest time and effort in personal growth.':
    'Disposición a invertir tiempo y esfuerzo en el crecimiento personal.',

  // — CORE-COMP-LOC-01 — remaining catalog competency names —
  'accountability': 'Responsabilidad por Resultados',
  'audit & compliance': 'Auditoría y Cumplimiento',
  'budgeting & forecasting': 'Presupuestación y Proyección',
  'business development': 'Desarrollo de Negocios',
  'change management': 'Gestión del Cambio',
  'client relationship management': 'Gestión de Relaciones con Clientes',
  'coaching & mentoring': 'Coaching y Mentoría',
  'collaboration': 'Colaboración',
  'conflict resolution': 'Resolución de Conflictos',
  'cost control': 'Control de Costos',
  'critical thinking': 'Pensamiento Crítico',
  'customer orientation': 'Orientación al Cliente',
  'cybersecurity awareness': 'Conciencia de Ciberseguridad',
  'data analysis': 'Análisis de Datos',
  'data privacy': 'Privacidad de Datos',
  'delegation': 'Delegación',
  'diversity & inclusion': 'Diversidad e Inclusión',
  'employee relations': 'Relaciones Laborales',
  'ethical conduct': 'Conducta Ética',
  'executive presence': 'Presencia Ejecutiva',
  'innovation': 'Innovación',
  'integrity': 'Integridad',
  'market awareness': 'Conocimiento del Mercado',
  'negotiation': 'Negociación',
  'operational excellence': 'Excelencia Operacional',
  'people leadership': 'Liderazgo de Personas',
  'problem solving': 'Resolución de Problemas',
  'process engineering': 'Ingeniería de Procesos',
  'professional judgment': 'Juicio Profesional',
  'project management': 'Gestión de Proyectos',
  'quality assurance': 'Aseguramiento de Calidad',
  'regulatory knowledge': 'Conocimiento Regulatorio',
  'research skills': 'Habilidades de Investigación',
  'risk management': 'Gestión de Riesgos',
  'safety & compliance': 'Seguridad y Cumplimiento',
  'sales execution': 'Ejecución de Ventas',
  'service recovery': 'Recuperación del Servicio',
  'software proficiency': 'Dominio de Software',
  'supply chain management': 'Gestión de Cadena de Suministro',
  'systems thinking': 'Pensamiento Sistémico',
  'talent assessment': 'Evaluación de Talento',
  'technical accounting judgment': 'Juicio Contable Técnico',
  'time management': 'Gestión del Tiempo',
  'vendor management': 'Gestión de Proveedores',
  'workforce planning': 'Planificación de la Fuerza Laboral',

  // — CORE-COMP-LOC-01 — remaining catalog competency descriptions —
  'owns outcomes, mistakes and commitments transparently.':
    'Asume resultados, errores y compromisos con transparencia.',
  'breaks complex topics into components, identifies cause and effect.':
    'Descompone temas complejos en componentes e identifica causa y efecto.',
  'maintains controls and evidence aligned with regulatory expectations.':
    'Mantiene controles y evidencia alineados con las expectativas regulatorias.',
  'plans, projects and tracks budgets against business objectives.':
    'Planifica, proyecta y da seguimiento a presupuestos frente a los objetivos del negocio.',
  'identifies and converts new commercial opportunities.':
    'Identifica y convierte nuevas oportunidades comerciales.',
  'leads transitions, manages resistance and embeds new ways of working.':
    'Lidera transiciones, gestiona la resistencia e incorpora nuevas formas de trabajo.',
  'builds long term, trust based client partnerships.':
    'Construye relaciones de largo plazo con clientes basadas en la confianza.',
  'develops talent through feedback, observation and structured guidance.':
    'Desarrolla talento mediante retroalimentación, observación y guía estructurada.',
  'works effectively with peers across functions and cultures.':
    'Trabaja eficazmente con colegas de distintas funciones y culturas.',
  'de escalates tension and turns disputes into constructive outcomes.':
    'Desescala tensiones y convierte disputas en resultados constructivos.',
  'identifies and manages cost drivers without compromising quality.':
    'Identifica y gestiona los generadores de costos sin comprometer la calidad.',
  'evaluates information rigorously to reach reasoned conclusions.':
    'Evalúa la información con rigor para llegar a conclusiones razonadas.',
  'centers decisions and design around customer needs and feedback.':
    'Centra las decisiones y el diseño en las necesidades y retroalimentación del cliente.',
  'recognizes, prevents and reports information security risks.':
    'Reconoce, previene y reporta riesgos de seguridad de la información.',
  'ability to gather, process and interpret quantitative data to inform decisions.':
    'Capacidad de recopilar, procesar e interpretar datos cuantitativos para informar decisiones.',
  'protects personal and confidential data per applicable regulation.':
    'Protege datos personales y confidenciales conforme a la regulación aplicable.',
  'distributes work with clarity, autonomy and accountability.':
    'Distribuye el trabajo con claridad, autonomía y responsabilidad.',
  'creates equitable environments where varied perspectives thrive.':
    'Crea entornos equitativos donde prosperan perspectivas diversas.',
  'builds and protects healthy, productive employee experiences.':
    'Construye y protege experiencias laborales sanas y productivas.',
  'acts with honesty and fairness in all professional dealings.':
    'Actúa con honestidad y equidad en todas las relaciones profesionales.',
  'communicates credibility, gravitas and composure with senior audiences.':
    'Comunica credibilidad, solidez y aplomo ante audiencias directivas.',
  'generates and refines new ideas that create measurable value.':
    'Genera y refina nuevas ideas que crean valor medible.',
  "acts consistently with the organization's values, even under pressure.":
    'Actúa de forma consistente con los valores de la organización, incluso bajo presión.',
  'structured analytical thinking, decomposition, and evidence based inference under ambiguity.':
    'Pensamiento analítico estructurado, descomposición e inferencia basada en evidencia bajo ambigüedad.',
  'tracks industry, competitive and customer trends to inform action.':
    'Da seguimiento a tendencias de la industria, la competencia y los clientes para orientar la acción.',
  'intrinsic and extrinsic drivers sustaining engagement.':
    'Impulsores intrínsecos y extrínsecos que sostienen el compromiso.',
  'reaches durable agreements that serve business and stakeholder interests.':
    'Alcanza acuerdos duraderos que sirven a los intereses del negocio y de las partes interesadas.',
  'sustains continuous improvement across processes and kpis.':
    'Sostiene la mejora continua en procesos e indicadores clave (KPIs).',
  'develops, motivates and aligns teams toward shared goals.':
    'Desarrolla, motiva y alinea equipos hacia metas compartidas.',
  'defines problems, generates options and selects effective solutions.':
    'Define problemas, genera opciones y selecciona soluciones eficaces.',
  'designs, optimizes and documents repeatable business processes.':
    'Diseña, optimiza y documenta procesos de negocio repetibles.',
  'applies experience and standards to interpret ambiguous situations responsibly.':
    'Aplica experiencia y estándares para interpretar situaciones ambiguas con responsabilidad.',
  'plans, executes and closes initiatives on scope, time and budget.':
    'Planifica, ejecuta y cierra iniciativas en alcance, tiempo y presupuesto.',
  'applies standards and methods to ensure reliable, compliant output.':
    'Aplica estándares y métodos para asegurar resultados confiables y conformes.',
  'maintains current expertise in relevant laws and regulations.':
    'Mantiene conocimiento actualizado de las leyes y regulaciones pertinentes.',
  'investigates problems methodically using credible sources and structured analysis.':
    'Investiga problemas metódicamente usando fuentes creíbles y análisis estructurado.',
  'identifies, assesses and mitigates financial and operational risks.':
    'Identifica, evalúa y mitiga riesgos financieros y operativos.',
  'maintains a safe workplace aligned with applicable regulation.':
    'Mantiene un lugar de trabajo seguro alineado con la regulación aplicable.',
  'drives the full sales cycle from qualification to close.':
    'Impulsa el ciclo completo de ventas, desde la calificación hasta el cierre.',
  'resolves service breakdowns to restore customer trust.':
    'Resuelve fallas de servicio para restaurar la confianza del cliente.',
  'operational mastery of the technical tools required for the role.':
    'Dominio operativo de las herramientas técnicas requeridas para el rol.',
  'capacity to sustain performance, priorities, and communication under sustained pressure.':
    'Capacidad de sostener desempeño, prioridades y comunicación bajo presión sostenida.',
  'optimizes flow of materials, information and resources end to end.':
    'Optimiza el flujo de materiales, información y recursos de extremo a extremo.',
  'understands how parts of an organization interact and influence outcomes.':
    'Comprende cómo interactúan las partes de una organización e influyen en los resultados.',
  'evaluates skills, fit and potential with structured methods.':
    'Evalúa habilidades, ajuste y potencial con métodos estructurados.',
  'sense of unity, trust and mutual support within the team.':
    'Sentido de unidad, confianza y apoyo mutuo dentro del equipo.',
  'applies accounting standards to non routine, judgment intensive situations.':
    'Aplica normas contables a situaciones no rutinarias que requieren juicio experto.',
  'prioritizes effort to deliver the most important work on time.':
    'Prioriza el esfuerzo para entregar a tiempo el trabajo más importante.',
  'selects, governs and develops third party partners.':
    'Selecciona, gobierna y desarrolla socios externos.',
  'forecasts capability needs and aligns hiring/development plans.':
    'Proyecta necesidades de capacidades y alinea planes de contratación y desarrollo.',
  'perceived fairness and sustainability of workload.':
    'Percepción de equidad y sostenibilidad de la carga de trabajo.',

  // — CORE-RES-LOC-01 — seeded official Resource Center titles —
  'getting started with corporate talent hub': 'Primeros Pasos con Corporate Talent Hub',
  'authoring a questionnaire': 'Creación de un Cuestionario',
  'from assignment to report release': 'De la Asignación a la Publicación del Informe',
  'frequently asked questions': 'Preguntas Frecuentes',
  'fixing common import and upload errors': 'Solución de errores comunes de importación y carga',
  'using ai in cth responsibly': 'Uso Responsable de la IA en CTH',
  'questionnaire import template (excel)': 'Plantilla de Importación de Cuestionarios (Excel)',
  'questionnaire import template (word)': 'Plantilla de Importación de Cuestionarios (Word)',
  'role profile template': 'Plantilla de Perfil de Rol',
  'leadership decision making (sample)': 'Liderazgo — Toma de Decisiones (ejemplo)',
  'team engagement pulse (sample)': 'Pulso de Compromiso del Equipo (ejemplo)',
  'operations competency assessment (sample)': 'Operaciones — Evaluación de Competencias (ejemplo)',
  'assessment studio module guide': 'Assessment Studio — Guía del Módulo',
  'evaluation library module guide': 'Biblioteca de Evaluaciones — Guía del Módulo',
  'resource center module guide': 'Centro de Recursos — Guía del Módulo',
  'role profiles module guide': 'Perfiles de Rol — Guía del Módulo',
  'competencies module guide': 'Competencias — Guía del Módulo',
  'how to create a position profile': 'Cómo Crear un Perfil de Posición',

  // — CORE-RES-LOC-01 — seeded official Resource Center descriptions —
  'a 10 minute onboarding for first time hm advisors. login, sidebar, suites, your first assignment.':
    'Una inducción de 10 minutos para Asesores HM nuevos. Inicio de sesión, barra lateral, suites y su primera asignación.',
  'a practical guide to creating production quality evaluations: sections, weights, scoring, and the lifecycle.':
    'Una guía práctica para crear evaluaciones con calidad de producción: secciones, pesos, puntuación y el ciclo de vida.',
  'the end to end flow: assign an evaluation, monitor progress, review the ai draft, release to the client.':
    'El flujo de extremo a extremo: asignar una evaluación, monitorear el avance, revisar el borrador de IA y publicar al cliente.',
  'quick answers to the 12 most common questions new hm advisors ask.':
    'Respuestas rápidas a las 12 preguntas más comunes de los nuevos Asesores HM.',
  'what to check when an .xlsx, .docx, or file upload fails inside the resource center or import wizard.':
    'Qué revisar cuando falla la carga de un .xlsx, .docx u otro archivo en el Centro de Recursos o el Asistente de Importación.',
  'how cth uses claude sonnet 4.5 for reports, what it can and cannot do, and when to override it.':
    'Cómo CTH usa Claude Sonnet 4.5 para informes, qué puede y no puede hacer, y cuándo anular su resultado.',
  'official excel template for the import wizard. includes the metadata sheet, sections, and questions sheets with two sample rows in each.':
    'Plantilla oficial de Excel para el Asistente de Importación. Incluye la hoja de metadatos y las hojas de secciones y preguntas con dos filas de ejemplo en cada una.',
  'official word template for the import wizard. use this when you prefer to draft questionnaires in prose before formalizing them.':
    'Plantilla oficial de Word para el Asistente de Importación. Úsela cuando prefiera redactar cuestionarios en prosa antes de formalizarlos.',
  "a structured template for capturing a role's responsibilities, competencies, and kpis before you build an evaluation.":
    'Una plantilla estructurada para capturar las responsabilidades, competencias y KPIs de un rol antes de construir una evaluación.',
  'a 12 question sample evaluation focused on decision quality under pressure. use it as is or import to library and adapt.':
    'Una evaluación de ejemplo de 12 preguntas enfocada en la calidad de decisión bajo presión. Úsela tal cual o impórtela a su biblioteca y adáptela.',
  'a 10 question pulse survey for measuring engagement on a single team. use weekly or monthly.':
    'Una encuesta de pulso de 10 preguntas para medir el compromiso de un equipo. Úsela semanal o mensualmente.',
  'a 13 question competency assessment for operations supervisors. three competencies: planning, escalation handling, and team development.':
    'Una evaluación de competencias de 13 preguntas para supervisores de operaciones. Tres competencias: planificación, manejo de escalamientos y desarrollo de equipo.',
  'the official guide to the studio: the unified authoring workspace, the editor, the questionnaire lifecycle, templates and samples.':
    'La guía oficial del Studio: el espacio unificado de creación, el editor, el ciclo de vida del cuestionario, plantillas y ejemplos.',
  'the official guide to the governed catalog: search and filters, lifecycle actions, suite compatibility, versioning and the reversible archive.':
    'La guía oficial del catálogo gobernado: búsqueda y filtros, acciones de ciclo de vida, compatibilidad de suites, versionado y archivo reversible.',
  'the official guide to the shared knowledge hub: categories, bilingual content, templates, samples, analytics, versions and permissions.':
    'La guía oficial del centro de conocimiento compartido: categorías, contenido bilingüe, plantillas, ejemplos, analítica, versiones y permisos.',
  'the official guide to role profiles: anatomy, competency requirements, weights and criticality, and how role fit verdicts are produced.':
    'La guía oficial de Perfiles de Rol: anatomía, requisitos de competencias, pesos y criticidad, y cómo se producen los veredictos de ajuste al rol.',
  'the official guide to the competency catalog: the shared scoring vocabulary, categories, governance rules and how competencies flow through the platform.':
    'La guía oficial del catálogo de competencias: el vocabulario compartido de puntuación, categorías, reglas de gobernanza y cómo fluyen las competencias por la plataforma.',
  'global vs company profiles, every section explained, how weight/expected/minimum/criticality drive position fit, and five worked examples.':
    'Perfiles globales vs. de empresa, cada sección explicada, cómo Peso/Esperado/Mínimo/Criticidad determinan el ajuste a la posición, y cinco ejemplos desarrollados.',

  // — Company names — leave proper nouns unchanged but normalize lookup —
  'infinity logistics': 'Infinity Logistics',
  'infinity logistics agency corp.': 'Infinity Logistics Agency Corp.',

  // — Seniority labels —
  'junior': 'Junior',
  'mid': 'Intermedio',
  'senior': 'Senior',
  'c level': 'Nivel C',
  'unassigned': 'Sin asignar',

  // — Common categories —
  'technical': 'técnica',
  'behavioral': 'conductual',
  'cognitive': 'cognitiva',
  'leadership_cat': 'liderazgo',
  'soft skill': 'habilidad blanda',
  'compliance': 'cumplimiento',

  // — Audit action labels —
  'report approved': 'Informe aprobado',
  'report released': 'Informe publicado',
  'report rejected': 'Informe rechazado',
  'report edited': 'Informe editado',
  'evaluation requested': 'Evaluación solicitada',
  'evaluation assigned': 'Evaluación asignada',
  'evaluation submitted': 'Evaluación enviada',
  'evaluation completed': 'Evaluación completada',
  'user created': 'Usuario creado',
  'user archived': 'Usuario archivado',
  'user updated': 'Usuario actualizado',
  'company created': 'Empresa creada',
  'company updated': 'Empresa actualizada',
  'permission granted': 'Permiso otorgado',
  'permission revoked': 'Permiso revocado',
  'login success': 'Inicio de sesión exitoso',
  'login failed': 'Inicio de sesión fallido',
  'consent accepted': 'Consentimiento aceptado',

  // — Suggestion titles (Intelligence / Decisions log "Action" column) —
  'at-risk profile':            'Perfil en riesgo',
  'consider role realignment':  'Considerar reasignación de rol',
  'on track — monitor':         'En curso — monitorear',
  'on track   monitor':         'En curso — monitorear',
  'performance bonus':          'Bono por desempeño',
  'performance-improvement plan':'Plan de mejora del desempeño',
  'performance improvement plan':'Plan de mejora del desempeño',
  'promote and retain':         'Promover y retener',
  'salary bump vs. market':     'Ajuste salarial vs. mercado',
  'salary bump vs market':      'Ajuste salarial vs. mercado',
  'stretch assignment':         'Asignación de mayor responsabilidad',
  'succession candidate':       'Candidato a sucesión',
  'targeted learning program':  'Programa de aprendizaje focalizado',

  // — Suggestion types (Intelligence / Decisions sub-label) —
  'bonus':            'bono',
  'monitor':          'monitorear',
  'promotion':        'promoción',
  'reassignment':     'reasignación',
  'risk':             'riesgo',
  'salary adjustment':'ajuste salarial',
  'salary_adjustment':'ajuste salarial',
  'succession':       'sucesión',
  'training':         'capacitación',

  // — Questionnaire / suite product names —
  'climara — work climate pulse':            'Climara — Pulso de clima laboral',
  'climara work climate pulse':              'Climara — Pulso de clima laboral',
  'talentcompetency — role benchmark':       'TalentCompetency — Benchmark de Puesto',
  'talentcompetency role benchmark':         'TalentCompetency — Benchmark de Puesto',
  'talenttrain — learning needs assessment': 'TalentTrain — Evaluación de Necesidades de Aprendizaje',
  'talenttrain learning needs assessment':   'TalentTrain — Evaluación de Necesidades de Aprendizaje',

  // — Missing-suite questionnaire names (5 new instruments) —
  // norm() collapses " — " (space + em-dash + space) into a single space, so
  // the lookup key for "Talent Check — Psychological..." is "talent check psychological..."
  'talent check psychological candidate & staff evaluation':
    'TalentCheck — Evaluación Psicológica de Candidatos y Personal',
  // P0 UAT 2026-08-11 — seeded questionnaire names use "TalentCheck" (no
  // space); both spellings must resolve or the candidate portal shows the
  // English instrument name in ES mode.
  'talentcheck psychological candidate & staff evaluation':
    'TalentCheck — Evaluación Psicológica de Candidatos y Personal',
  'talentstart onboarding experience check':
    'TalentStart — Evaluación de Experiencia de Incorporación',
  'talentheart employee loyalty & recognition pulse':
    'TalentHeart — Pulso de Lealtad y Reconocimiento',
  'talentfix team health & conflict diagnostic':
    'TalentFix — Diagnóstico de Salud y Conflicto del Equipo',
  'talentintelligence performance & roi self assessment':
    'TalentIntelligence — Autoevaluación de Desempeño y ROI',

  // — Missing-suite questionnaire descriptions —
  'psychological profile for candidates (pre hire) and existing staff. covers cognitive style, emotional intelligence, work values and leadership orientation. includes a voluntary \'get to know me\' section.':
    'Perfil psicológico para candidatos (previo a la contratación) y personal actual. Cubre estilo cognitivo, inteligencia emocional, valores de trabajo y orientación al liderazgo. Incluye una sección voluntaria "Conóceme".',
  'measures quality of the onboarding journey at 30, 60 and 90 days. surfaces clarity, integration and early commitment signals.':
    'Mide la calidad del proceso de incorporación a los 30, 60 y 90 días. Detecta claridad, integración y señales tempranas de compromiso.',
  'detects flight risk and recognition preferences. recommended every quarter for active collaborators.':
    'Detecta el riesgo de fuga e identifica preferencias de reconocimiento. Recomendado cada trimestre para colaboradores activos.',
  'diagnoses team dysfunction, conflict level and psychological safety before designing a targeted intervention.':
    'Diagnostica disfunciones del equipo, nivel de conflicto y seguridad psicológica antes de diseñar una intervención focalizada.',
  'collects self reported performance and goal achievement data to complement objective kpi dashboards. recommended every semester.':
    'Recoge datos autorreportados de desempeño y cumplimiento de objetivos para complementar los tableros objetivos de KPIs. Recomendado cada semestre.',

  // — Suite descriptions (from seed.py) —
  'integral talent evaluation across competency areas.':
    'Evaluación integral de talento en distintas áreas de competencia.',
  'pre hire fit to role and reference check workflow.':
    'Flujo de ajuste al puesto y verificación de referencias previo a la contratación.',
  'structured onboarding journey for new hires.':
    'Recorrido de onboarding estructurado para nuevas incorporaciones.',
  'engagement & climate pulse surveys.':
    'Encuestas de pulso de compromiso y clima.',
  'engagement and climate pulse surveys.':
    'Encuestas de pulso de compromiso y clima.',
  'competency framework & 360° assessments.':
    'Marco de competencias y evaluaciones 360°.',
  'competency framework and 360° assessments.':
    'Marco de competencias y evaluaciones 360°.',
  'personalized learning paths from assessment gaps.':
    'Rutas de aprendizaje personalizadas a partir de las brechas de evaluación.',
  'wellbeing and emotional health diagnostics.':
    'Diagnósticos de bienestar y salud emocional.',
  'targeted intervention plans for at risk teams.':
    'Planes de intervención focalizados para equipos en riesgo.',
  'executive dashboard: cost vs. contribution analysis.':
    'Tablero ejecutivo: análisis de costo vs. contribución.',
  'executive dashboard cost vs contribution analysis.':
    'Tablero ejecutivo: análisis de costo vs. contribución.',

  // — Auto-generated role-profile position titles —
  'academic advisor': 'Asesor Académico',
  'administrative assistant': 'Asistente Administrativo',
  'associate attorney': 'Abogado Asociado',
  'auditor': 'Auditor',
  'by consultant': 'Por Asesor',
  'cashier lead': 'Líder de Caja',
  'civil engineer': 'Ingeniero Civil',
  'clinical director': 'Director Clínico',
  'compliance officer': 'Oficial de Cumplimiento',
  'contract manager': 'Gerente de Contratos',
  'curriculum coordinator': 'Coordinador de Currículo',
  'customer experience lead': 'Líder de Experiencia del Cliente',
  'customer success manager': 'Gerente de Éxito del Cliente',
  'department head': 'Jefe de Departamento',
  'devops engineer': 'Ingeniero DevOps',
  'driver supervisor': 'Supervisor de Conductores',
  'education administrator': 'Administrador Educativo',
  'fleet manager': 'Gerente de Flota',
  'foreman': 'Capataz',
  'general manager': 'Gerente General',
  'hr business partner': 'Socio de Negocios de RR. HH.',
  'healthcare administrator': 'Administrador de Salud',
  'inventory coordinator': 'Coordinador de Inventario',
  'investment advisor': 'Asesor de Inversiones',
  'legal operations lead': 'Líder de Operaciones Legales',
  'logistics coordinator': 'Coordinador de Logística',
  'marketing manager': 'Gerente de Marketing',
  'medical records specialist': 'Especialista en Registros Médicos',
  'nurse manager': 'Gerente de Enfermería',
  'operations lead': 'Líder de Operaciones',
  'operations manager': 'Gerente de Operaciones',
  'paralegal': 'Asistente Legal',
  'patch target': 'Objetivo de Parche',
  'patient care coordinator': 'Coordinador de Cuidado al Paciente',
  'physician': 'Médico',
  'product manager': 'Gerente de Producto',
  'project manager': 'Gerente de Proyecto',
  'qa lead': 'Líder de Calidad',
  'quantity surveyor': 'Cuantificador de Obras',
  'risk analyst': 'Analista de Riesgo',
  'safety officer': 'Oficial de Seguridad',
  'sales associate': 'Asociado de Ventas',
  'school principal': 'Director de Escuela',
  'senior accountant': 'Contador Senior',
  'senior counsel': 'Asesor Legal Senior',
  'senior developer': 'Desarrollador Senior',
  'site supervisor': 'Supervisor de Obra',
  'software engineer': 'Ingeniero de Software',
  'store manager': 'Gerente de Tienda',
  'supply chain analyst': 'Analista de Cadena de Suministro',
  'teacher': 'Docente',
  'treasury analyst': 'Analista de Tesorería',
  'ux designer': 'Diseñador UX',
  'visual merchandiser': 'Visual Merchandiser',
  'warehouse supervisor': 'Supervisor de Almacén',

  // — Auto-generated role-profile departments —
  'academics': 'Académico',
  'accounting': 'Contabilidad',
  'administration': 'Administración',
  'audit': 'Auditoría',
  'cx': 'Experiencia del Cliente',
  'clinical': 'Clínico',
  'compliance': 'Cumplimiento',
  'costs': 'Costos',
  'customer success': 'Éxito del Cliente',
  'design': 'Diseño',
  'engineering': 'Ingeniería',
  'faculty': 'Facultad',
  'finance': 'Finanzas',
  'finance & logistics operations': 'Finanzas y Operaciones Logísticas',
  'fleet': 'Flota',
  'hse': 'HSE (Salud, Seguridad y Medio Ambiente)',
  'leadership': 'Liderazgo',
  'legal': 'Legal',
  'legal support': 'Apoyo Legal',
  'marketing': 'Marketing',
  'medical': 'Médico',
  'nursing': 'Enfermería',
  'operations': 'Operaciones',
  'people': 'Personas',
  'product': 'Producto',
  'projects': 'Proyectos',
  'risk': 'Riesgo',
  'sales': 'Ventas',
  'strategy': 'Estrategia',
  'student services': 'Servicios Estudiantiles',
  'treasury': 'Tesorería',
  'warehouse': 'Almacén',
  'wealth': 'Patrimonio',

};

export function useDataLabel() {
  const { i18n } = useTranslation();
  const isES = (i18n.language || '').startsWith('es');
  return (value, fallback = '—') => {
    if (value == null || value === '') return fallback;
    // Session N-B — support bilingual data objects: { name, name_es }.
    // Caller passes the whole record so we can prefer the DB-level
    // translation when present, then fall back to the seeded lookup
    // map, then to the canonical English value.
    if (typeof value === 'object') {
      const en = value.name ?? value.label ?? value.title ?? '';
      const es = value.name_es ?? value.label_es ?? value.title_es ?? '';
      if (!isES) return en || fallback;
      if (es) return es;
      // No explicit ES translation — try the seeded lookup, else fall back to EN.
      const key = norm(en);
      return ES_MAP[key] || en || fallback;
    }
    if (!isES) return value;
    const key = norm(value);
    return ES_MAP[key] || value;
  };
}

/**
 * Session N-B — convenience helper for the most common shape: a department
 * (or any entity) with `name` + `name_es` fields. Use when you have a raw
 * doc; for pre-flattened payloads (department_name, department_name_es)
 * pass `{ name: row.department_name, name_es: row.department_name_es }`.
 */
export function useDeptLabel() {
  const tData = useDataLabel();
  return (dept, fallback = '—') => {
    if (!dept) return fallback;
    if (typeof dept === 'string') return tData(dept, fallback);
    return tData(dept, fallback);
  };
}
